import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SearchBar from '../components/SearchBar';
import BookCard from '../components/BookCard';
import AdvancedSearch from '../components/AdvancedSearch';
import { buildApiUrl, buildAssetUrl, getPlaceholderImage } from '../config/constants';

// Interfața pentru obiectul carte
interface Book {
  id: number;
  title: string;
  author: string;
  description: string;
  coverImage?: string;
  category?: string;
  tags?: string[] | string;
  publishedYear?: number;
  language?: string;
  pages?: number;
  fileName?: string;
  fileType?: string;
}

interface Category {
  category: string;
  count: number;
}

// Date fictive pentru fallback
const mockBooks = [
  {
    id: 1,
    title: "Micul Prinț",
    author: "Antoine de Saint-Exupéry",
    description: "O poveste despre prietenie, iubire și responsabilitate, spusă prin ochii unui pilot care se prăbușește în deșert și întâlnește un tânăr prinț de pe o planetă îndepărtată.",
    coverImage: "https://m.media-amazon.com/images/I/71OZY035QKL._AC_UF1000,1000_QL80_.jpg",
    category: "Ficțiune",
    publishedYear: 1943,
    language: "Română",
    pages: 96
  },
  {
    id: 2,
    title: "1984",
    author: "George Orwell",
    description: "Un roman distopic care prezintă un viitor sumbru în care guvernul totalitar, condus de misteriosul Big Brother, controlează fiecare aspect al vieții cetățenilor.",
    coverImage: "https://m.media-amazon.com/images/I/71kxa1-0mfL._AC_UF1000,1000_QL80_.jpg",
    category: "Distopie",
    publishedYear: 1949,
    language: "Română",
    pages: 328
  },
  {
    id: 3,
    title: "Mândrie și prejudecată",
    author: "Jane Austen",
    description: "O satiră a moravurilor sociale și o poveste de dragoste între Elizabeth Bennet și orgoliosul domn Darcy, într-o societate în care căsătoria era adesea o necesitate economică.",
    coverImage: "https://m.media-amazon.com/images/I/71Q1tPupKjL._AC_UF1000,1000_QL80_.jpg",
    category: "Clasic",
    publishedYear: 1813,
    language: "Română",
    pages: 432
  },
  {
    id: 4,
    title: "Amintiri din copilărie",
    author: "Ion Creangă",
    description: "Povestiri autobiografice care descriu copilăria autorului într-un sat din Moldova, capturând frumusețea vieții rurale și a tradițiilor românești.",
    coverImage: "https://cdn.dc5.ro/img-prod/1018849893-0.jpeg",
    category: "Memorialistică",
    publishedYear: 1892,
    language: "Română",
    pages: 180
  },
  {
    id: 5,
    title: "Hobbitul",
    author: "J.R.R. Tolkien",
    description: "Aventura lui Bilbo Baggins, un hobbit care pornește într-o călătorie neașteptată pentru a recupera comoara păzită de dragonul Smaug.",
    coverImage: "https://m.media-amazon.com/images/I/710+HcoP38L._AC_UF1000,1000_QL80_.jpg",
    category: "Fantasy",
    publishedYear: 1937,
    language: "Română",
    pages: 310
  },
  {
    id: 6,
    title: "Sapiens: Scurtă istorie a omenirii",
    author: "Yuval Noah Harari",
    description: "O explorare a istoriei umane, de la apariția speciei Homo sapiens până în prezent, analizând impactul revoluțiilor cognitive, agricole și științifice.",
    coverImage: "https://m.media-amazon.com/images/I/71N3-2sYxQL._AC_UF1000,1000_QL80_.jpg",
    category: "Non-Ficțiune",
    publishedYear: 2011,
    language: "Română",
    pages: 464
  },
  {
    id: 7,
    title: "Stăpânul inelelor: Frăția inelului",
    author: "J.R.R. Tolkien",
    description: "Primul volum al trilogiei epice care urmărește călătoria lui Frodo Baggins pentru a distruge Inelul Puterii și a salva Pământul de Mijloc de forțele întunecate ale lui Sauron.",
    coverImage: "https://m.media-amazon.com/images/I/71KUk3+4X+L._AC_UF1000,1000_QL80_.jpg",
    category: "Fantasy",
    publishedYear: 1954,
    language: "Română",
    pages: 576
  },
  {
    id: 8,
    title: "O mie nouă sute optzeci și patru",
    author: "George Orwell",
    description: "Ediția aniversară a clasicului distopic care prezintă un viitor totalitar, unde guvernul supraveghează constant cetățenii și manipulează adevărul.",
    coverImage: "https://cdn.dc5.ro/img-prod/9786067795646-0.jpeg",
    category: "Distopie",
    publishedYear: 1949,
    language: "Română",
    pages: 352
  },
  {
    id: 9,
    title: "Fahrenheit 451",
    author: "Ray Bradbury",
    description: "Un roman distopic despre un viitor în care cărțile sunt interzise, iar 'pompierii' au sarcina de a le arde pentru a controla gândirea și cunoașterea.",
    coverImage: "https://m.media-amazon.com/images/I/71OFqSRFDlL._AC_UF1000,1000_QL80_.jpg",
    category: "Distopie",
    publishedYear: 1953,
    language: "Română",
    pages: 256
  },
  {
    id: 10,
    title: "Crima din Orient Express",
    author: "Agatha Christie",
    description: "Un celebru roman polițist în care detectivul Hercule Poirot investighează o crimă misterioasă petrecută la bordul faimosului tren Orient Express.",
    coverImage: "https://cdn.dc5.ro/img-prod/9786063333378-0.jpeg",
    category: "Mister",
    publishedYear: 1934,
    language: "Română",
    pages: 288
  },
  {
    id: 11,
    title: "Dune",
    author: "Frank Herbert",
    description: "Un roman SF complex care explorează politica, religia și ecologia pe planeta deșertică Arrakis, unde Spice-ul, o substanță valoroasă, este exploatat.",
    coverImage: "https://m.media-amazon.com/images/I/A1K7Cf+G8iL._AC_UF1000,1000_QL80_.jpg",
    category: "Science Fiction",
    publishedYear: 1965,
    language: "Română",
    pages: 624
  },
  {
    id: 12,
    title: "Alchimistul",
    author: "Paulo Coelho",
    description: "Povestea unui tânăr păstor andaluz care călătorește în căutarea unei comori, descoperind în final că adevărata comoară se află în călătoria însăși și în lecțiile învățate.",
    coverImage: "https://cdn.dc5.ro/img-prod/1033800-0.jpeg",
    category: "Ficțiune",
    publishedYear: 1988,
    language: "Română",
    pages: 224
  }
];

const BooksPage: React.FC = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const categoryFromUrl = queryParams.get('category');
  
  const [books, setBooks] = useState<Book[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filteredBooks, setFilteredBooks] = useState<Book[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>(
    categoryFromUrl ? [categoryFromUrl] : []
  );
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
  const [selectedYearRange, setSelectedYearRange] = useState<{min: number, max: number}>({min: 1900, max: 2023});
  const [sortBy, setSortBy] = useState("year-desc");
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [selectedBook, setSelectedBook] = useState<any | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const booksPerPage = 6;

  // Încărcăm cărțile din backend
  useEffect(() => {
    const fetchBooks = async () => {
      try {
        setLoading(true);
        console.log('Fetching books from API:', buildApiUrl('/books'));
        
        const response = await fetch(buildApiUrl('/books'), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Origin': window.location.origin
          },
          mode: 'cors',
          credentials: 'omit' // Change to 'include' if you need to send cookies
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('Received books data:', data);
        
        // Verifică dacă data este un array și conține cărți
        if (Array.isArray(data) && data.length > 0) {
          setBooks(data);
          setFilteredBooks(data);
        } else if (data && data.books && Array.isArray(data.books)) {
          setBooks(data.books);
          setFilteredBooks(data.books);
        } else {
          console.warn('No books found in API response, using mock data');
          setBooks(mockBooks);
          setFilteredBooks(mockBooks);
        }
      } catch (err: any) {
        console.error('Eroare la încărcarea cărților:', err);
        setError('Nu s-au putut încărca cărțile. Folosim date demo.');
        setBooks(mockBooks);
        setFilteredBooks(mockBooks); // Set filtered books directly to ensure they display
      } finally {
        setLoading(false);
      }
    };
    
    fetchBooks();
  }, []);

  // Încărcăm categoriile din backend
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(buildApiUrl('/books/categories'));
        if (response.ok) {
          const data = await response.json();
          setCategories(data);
        } else {
          // Fallback la categoriile calculate din cărțile existente
          const allCategories = Array.from(new Set(
            books.map(book => book.category || 'Necategorizat')
          ));
          const formattedCategories = allCategories.map(category => ({
            category,
            count: books.filter(book => (book.category || 'Necategorizat') === category).length
          }));
          setCategories(formattedCategories);
        }
      } catch (error) {
        console.error('Eroare la încărcarea categoriilor:', error);
        // Fallback la categoriile calculate din cărțile existente
        const allCategories = Array.from(new Set(
          books.map(book => book.category || 'Necategorizat')
        ));
        const formattedCategories = allCategories.map(category => ({
          category,
          count: books.filter(book => (book.category || 'Necategorizat') === category).length
        }));
        setCategories(formattedCategories);
      }
    };

    if (books.length > 0) {
      fetchCategories();
    }
  }, [books]);

  // Categorii pentru filtrare - extrase din state
  const allCategories = categories.map(cat => cat.category);
  
  // Limbi bazate pe cărțile existente
  const allLanguages = Array.from(new Set(
    books.map(book => book.language || 'Română').filter(Boolean)
  ));

  // Intervalul de ani bazat pe cărțile existente
  const yearRange = React.useMemo(() => {
    const publishedYears = books
      .map(book => book.publishedYear)
      .filter((year): year is number => year !== undefined && year !== null);
    
    if (publishedYears.length === 0) {
      // Default range if no years available
      return { min: 1900, max: new Date().getFullYear() };
    }
    
    return {
      min: Math.min(...publishedYears),
      max: Math.max(...publishedYears)
    };
  }, [books]);

  // Opțiuni de sortare
  const sortOptions = [
    { label: "Cele mai noi", value: "year-desc" },
    { label: "Cele mai vechi", value: "year-asc" },
    { label: "Titlu (A-Z)", value: "title-asc" },
    { label: "Titlu (Z-A)", value: "title-desc" },
    { label: "Autor (A-Z)", value: "author-asc" },
    { label: "Autor (Z-A)", value: "author-desc" }
  ];

  // Efect pentru aplicarea filtrelor
  useEffect(() => {
    if (books.length === 0) return;
    
    console.log('Filtering books, starting with', books.length, 'books');
    let results = [...books];
    
    // Căutare text
    if (searchQuery) {
      results = results.filter(book => 
        book.title?.toLowerCase().includes(searchQuery.toLowerCase()) || 
        book.author?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        book.description?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      console.log('After text search:', results.length, 'books remain');
    }
    
    // Filtrare pe categorii
    if (selectedCategories.length > 0) {
      results = results.filter(book => {
        const bookCategory = book.category || 'Necategorizat';
        return selectedCategories.includes(bookCategory);
      });
      console.log('After category filter:', results.length, 'books remain');
    }
    
    // Filtrare pe limbi
    if (selectedLanguages.length > 0) {
      results = results.filter(book => book.language && selectedLanguages.includes(book.language));
      console.log('After language filter:', results.length, 'books remain');
    }
    
    // Filtrare pe ani
    results = results.filter(book => {
      const year = book.publishedYear;
      return year === undefined || (year >= selectedYearRange.min && year <= selectedYearRange.max);
    });
    console.log('After year filter:', results.length, 'books remain');
    
    // Sortare
    results = sortBooks(results, sortBy);
    console.log('Setting filtered books:', results);
    
    setFilteredBooks(results);
    setCurrentPage(1); // Reset la prima pagină când se schimbă filtrele
  }, [books, searchQuery, selectedCategories, selectedLanguages, selectedYearRange, sortBy]);

  // Actualizare când parametrii URL se schimbă
  useEffect(() => {
    if (categoryFromUrl && !selectedCategories.includes(categoryFromUrl)) {
      setSelectedCategories([categoryFromUrl]);
    }
  }, [location.search, categoryFromUrl]);

  // Funcția de sortare
  const sortBooks = (books: Book[], sortOption: string) => {
    const [field, direction] = sortOption.split('-');
    
    return [...books].sort((a, b) => {
      let comparison = 0;
      
      if (field === 'year') {
        const yearA = a.publishedYear || 0;
        const yearB = b.publishedYear || 0;
        comparison = yearA - yearB;
      } else if (field === 'title') {
        comparison = (a.title || '').localeCompare(b.title || '');
      } else if (field === 'author') {
        comparison = (a.author || '').localeCompare(b.author || '');
      }
      
      return direction === 'asc' ? comparison : -comparison;
    });
  };

  // Funcții pentru gestionarea filtrelor
  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(cat => cat !== category) 
        : [...prev, category]
    );
  };

  const handleLanguageChange = (language: string) => {
    setSelectedLanguages(prev => 
      prev.includes(language) 
        ? prev.filter(lang => lang !== language) 
        : [...prev, language]
    );
  };

  const handleYearRangeChange = (min: number, max: number) => {
    setSelectedYearRange({ min, max });
  };

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSortBy(e.target.value);
  };

  const resetFilters = () => {
    setSearchQuery('');
    setSelectedCategories([]);
    setSelectedLanguages([]);
    setSelectedYearRange(yearRange);
    setSortBy('year-desc');
  };

  const handleAdvancedSearch = (filters: any) => {
    if (filters.title) setSearchQuery(filters.title);
    if (filters.genre) setSelectedCategories([filters.genre]);
    if (filters.language) setSelectedLanguages([filters.language]);
    if (filters.year) {
      setSelectedYearRange({
        min: filters.year.min || yearRange.min,
        max: filters.year.max || yearRange.max
      });
    }
    
    setShowAdvancedSearch(false);
  };

  const handleBookClick = (bookId: number) => {
    const book = books.find(b => b.id === bookId);
    if (book) {
      setSelectedBook({
        ...book,
        content: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.",
        totalPages: book.pages,
        currentPage: 1
      });
    }
  };

  // Paginarea
  const indexOfLastBook = currentPage * booksPerPage;
  const indexOfFirstBook = indexOfLastBook - booksPerPage;
  const currentBooks = filteredBooks.slice(indexOfFirstBook, indexOfLastBook);
  const totalPages = Math.ceil(filteredBooks.length / booksPerPage);

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  // Adaugă o imagine placeholder pentru cărțile fără copertă
  const getBookCoverImage = (book: Book) => {
    console.log('Getting cover image for book:', book.title, 'coverImage:', book.coverImage);
    if (book.coverImage) return buildAssetUrl(book.coverImage);
    return getPlaceholderImage(book.title, 300, 450);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      {/* Spațiu pentru header-ul fix */}
      <div className="pt-24 md:pt-28"></div>
      
      <main className="flex-grow">
        {/* Hero section */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 py-12 border-b">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Explorează Biblioteca Noastră Digitală</h1>
              <p className="text-lg text-gray-600 mb-6">
                Descoperă o lume întreagă a cunoașterii prin colecția noastră vastă de cărți și resurse academice
              </p>
              
              {/* SearchBar */}
              <div className="mb-6">
                <SearchBar onSearch={handleSearch} />
              </div>
              
              {/* Buton căutare avansată */}
              <button 
                onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
                className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                {showAdvancedSearch ? 'Ascunde căutarea avansată' : 'Căutare avansată'}
              </button>
            </div>
          </div>
        </div>
        
        {/* Formularul de căutare avansată */}
        {showAdvancedSearch && (
          <div className="py-6 border-b bg-gray-50">
            <div className="container mx-auto px-4">
              <AdvancedSearch 
                onApplyFilters={handleAdvancedSearch} 
                onClose={() => setShowAdvancedSearch(false)} 
                initialCategory={categoryFromUrl || ''}
              />
            </div>
          </div>
        )}
        
        {/* Main content */}
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row">
            {/* Sidebar cu filtre */}
            <div className="md:w-1/4 md:pr-8 mb-8 md:mb-0">
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-lg font-bold text-gray-800">Filtre</h2>
                  <button 
                    onClick={resetFilters}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    Resetează
                  </button>
                </div>
                
                {/* Sortare */}
                <div className="mb-6">
                  <label htmlFor="sort" className="block text-sm font-medium text-gray-700 mb-2">
                    Sortare
                  </label>
                  <select
                    id="sort"
                    value={sortBy}
                    onChange={handleSortChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    {sortOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Categorii */}
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Categorii</h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {categories.map(({ category, count }) => (
                      <div key={category} className="flex items-center justify-between">
                        <div className="flex items-center">
                          <input
                            id={`category-${category}`}
                            type="checkbox"
                            checked={selectedCategories.includes(category)}
                            onChange={() => handleCategoryChange(category)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label
                            htmlFor={`category-${category}`}
                            className="ml-2 block text-sm text-gray-700"
                          >
                            {category}
                          </label>
                        </div>
                        <span className="text-xs text-gray-500">({count})</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Limba */}
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Limba</h3>
                  <div className="space-y-2">
                    {allLanguages.map(language => (
                      <div key={language} className="flex items-center">
                        <input
                          id={`language-${language}`}
                          type="checkbox"
                          checked={selectedLanguages.includes(language)}
                          onChange={() => handleLanguageChange(language)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label
                          htmlFor={`language-${language}`}
                          className="ml-2 block text-sm text-gray-700"
                        >
                          {language}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Anul publicării */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Anul publicării</h3>
                  <div className="flex items-center space-x-2">
                    <input
                      type="number"
                      value={selectedYearRange.min}
                      onChange={(e) => handleYearRangeChange(parseInt(e.target.value) || yearRange.min, selectedYearRange.max)}
                      min={yearRange.min}
                      max={yearRange.max}
                      className="w-24 px-2 py-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                    <span className="text-gray-500">-</span>
                    <input
                      type="number"
                      value={selectedYearRange.max}
                      onChange={(e) => handleYearRangeChange(selectedYearRange.min, parseInt(e.target.value) || yearRange.max)}
                      min={yearRange.min}
                      max={yearRange.max}
                      className="w-24 px-2 py-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Lista de cărți */}
            <div className="md:w-3/4">
              <div className="mb-6 flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-800">
                  {searchQuery 
                    ? `Rezultate pentru "${searchQuery}"`
                    : 'Toate cărțile'}
                  <span className="text-gray-500 text-base font-normal ml-2">
                    ({filteredBooks.length} cărți)
                  </span>
                </h2>
              </div>
              
              {loading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : filteredBooks.length === 0 ? (
                <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-200 text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Niciun rezultat găsit</h3>
                  <p className="text-gray-600 mb-4">
                    Nu am găsit nicio carte care să corespundă criteriilor tale de căutare.
                  </p>
                  <button 
                    onClick={resetFilters}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Resetează filtrele
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {currentBooks.map(book => (
                      <div key={book.id} onClick={() => handleBookClick(book.id)}>
                        <BookCard 
                          id={book.id}
                          title={book.title}
                          author={book.author}
                          description={book.description || 'Fără descriere'}
                          coverImage={book.coverImage || ''}
                          category={book.category || 'Necategorizat'}
                          fileName={book.fileName}
                          fileType={book.fileType}
                        />
                      </div>
                    ))}
                  </div>
                  
                  {/* Paginare */}
                  {totalPages > 1 && (
                    <div className="mt-10 flex justify-center">
                      <nav className="inline-flex rounded-md shadow">
                        <button
                          onClick={() => paginate(Math.max(1, currentPage - 1))}
                          disabled={currentPage === 1}
                          className={`relative inline-flex items-center px-3 py-2 rounded-l-md border ${
                            currentPage === 1
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-white text-gray-700 hover:bg-gray-50'
                          } text-sm font-medium focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500`}
                        >
                          <span className="sr-only">Anterior</span>
                          <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </button>
                        
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNumber => (
                          <button
                            key={pageNumber}
                            onClick={() => paginate(pageNumber)}
                            className={`relative inline-flex items-center px-4 py-2 border ${
                              currentPage === pageNumber
                                ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                            } text-sm font-medium focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500`}
                          >
                            {pageNumber}
                          </button>
                        ))}
                        
                        <button
                          onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
                          disabled={currentPage === totalPages}
                          className={`relative inline-flex items-center px-3 py-2 rounded-r-md border ${
                            currentPage === totalPages
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-white text-gray-700 hover:bg-gray-50'
                          } text-sm font-medium focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500`}
                        >
                          <span className="sr-only">Următor</span>
                          <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </nav>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default BooksPage; 
